import { cardName, type Card as CardCode, type Suit } from '../engine';

export const SUIT_GLYPH: Record<Suit, string> = { C: '♣', D: '♦', H: '♥', S: '♠' };
const RED = new Set(['D', 'H']);

function parts(card: CardCode): { corner: string; glyph: string; red: boolean } {
  if (card === 'BJ' || card === 'LJ') return { corner: '★', glyph: '★', red: card === 'BJ' };
  const rank = card[0] === 'T' ? '10' : card[0]!;
  const suit = card[1] as Suit;
  return { corner: rank, glyph: SUIT_GLYPH[suit], red: RED.has(suit) };
}

/** A playing card drawn in SVG (no image files). Clickable when `onClick` is set. */
export function Card({
  card,
  width = 64,
  selected = false,
  disabled = false,
  dim = false,
  onClick,
  testid,
  extraClass = '',
  tag,
  style,
}: {
  card: CardCode;
  width?: number;
  selected?: boolean;
  disabled?: boolean;
  /** Greyed out but still shown (e.g. not playable under the named suit). */
  dim?: boolean;
  onClick?: () => void;
  testid?: string;
  /** More classes, e.g. `called` for the glowing called card. */
  extraClass?: string;
  /** A short label drawn across the bottom, e.g. "CALLED". */
  tag?: string;
  style?: Record<string, string | number>;
}) {
  const h = width * 1.4;
  const { corner, glyph, red } = parts(card);
  const color = red ? '#c1121f' : '#1b1b1b';
  const classes = ['card', selected ? 'selected' : '', onClick && !disabled ? 'clickable' : '', dim ? 'dim' : '', extraClass].join(' ');
  return (
    <svg
      class={classes}
      viewBox={`0 0 ${width} ${h}`}
      width={width}
      height={h}
      role={onClick ? 'button' : 'img'}
      aria-label={cardName(card)}
      aria-pressed={onClick ? selected : undefined}
      aria-disabled={onClick ? disabled : undefined}
      data-card={card}
      data-testid={testid}
      style={style}
      tabindex={onClick && !disabled ? 0 : undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={
        onClick && !disabled
          ? (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <rect x="1" y="1" width={width - 2} height={h - 2} rx={width * 0.08} fill="#fffdf7" stroke="#3d3d3d" stroke-width="1.5" />
      <text x={width * 0.1} y={h * 0.17} font-size={width * 0.22} font-weight="700" fill={color}>
        {corner}
      </text>
      {corner !== glyph && (
        <text x={width * 0.1} y={h * 0.31} font-size={width * 0.2} fill={color}>
          {glyph}
        </text>
      )}
      {card === 'BJ' || card === 'LJ' ? (
        <>
          <text x={width / 2} y={h * 0.55} text-anchor="middle" font-size={width * 0.36} fill={color}>
            {glyph}
          </text>
          <text x={width / 2} y={h * 0.72} text-anchor="middle" font-size={width * 0.19} font-weight="700" fill={color}>
            JOKER
          </text>
          <text x={width / 2} y={h * 0.87} text-anchor="middle" font-size={width * 0.16} font-weight="600" fill={color}>
            {card === 'BJ' ? 'HIGH' : 'LOW'}
          </text>
        </>
      ) : (
        <text x={width / 2} y={h * 0.66} text-anchor="middle" font-size={width * 0.52} fill={color}>
          {glyph}
        </text>
      )}
      {tag && (
        <>
          <rect x="2" y={h * 0.8} width={width - 4} height={h * 0.16} rx="3" fill="#e9c46a" />
          <text x={width / 2} y={h * 0.925} text-anchor="middle" font-size={width * 0.16} font-weight="800" fill="#1b1b1b">
            {tag}
          </text>
        </>
      )}
    </svg>
  );
}

/** A face-down card, for other players' hands and the kitty. */
export function CardBack({ width = 28 }: { width?: number }) {
  const h = width * 1.4;
  return (
    <svg class="card back" viewBox={`0 0 ${width} ${h}`} width={width} height={h} aria-hidden="true">
      <rect x="1" y="1" width={width - 2} height={h - 2} rx={width * 0.1} fill="#264653" stroke="#f1faee" stroke-width="1.5" />
      <rect x={width * 0.18} y={h * 0.13} width={width * 0.64} height={h * 0.74} rx={width * 0.06} fill="none" stroke="#e9c46a" stroke-width="1" />
    </svg>
  );
}
